class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.originalButton = null;
    this.stickyButton = null;
    this.productInfo = null;
    this.threshold = 0;
    this.init();
  }

  init() {
    // Get references to key elements
    this.originalButton = document.querySelector('#ProductSubmitButton-template--main');
    if (!this.originalButton) {
      this.originalButton = document.querySelector('[name="add"]');
    }

    this.stickyButton = this.querySelector('.sticky-add-to-cart__button');
    this.productInfo = document.querySelector('.product__info-container');

    if (!this.originalButton || !this.stickyButton) {
      console.warn('Sticky Add to Cart: Required elements not found');
      return;
    }

    // Set up scroll listener
    this.setupScrollListener();

    // Sync button states
    this.syncButtonState();

    // Set up click handler for sticky button
    this.stickyButton.addEventListener('click', this.handleStickyButtonClick.bind(this));

    // Watch for changes to the original button
    this.observeOriginalButton();
  }

  setupScrollListener() {
    // Calculate threshold based on original button position
    const calculateThreshold = () => {
      if (this.originalButton) {
        const rect = this.originalButton.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        this.threshold = rect.top + scrollTop + rect.height;
      }
    };

    calculateThreshold();

    // Recalculate on resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        calculateThreshold();
        this.handleScroll();
      }, 250);
    });

    // Handle scroll
    this.handleScroll = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.handleScroll, { passive: true });

    // Initial check
    this.handleScroll();
  }

  handleScroll() {
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    if (scrollPosition > this.threshold) {
      this.show();
    } else {
      this.hide();
    }
  }

  show() {
    this.classList.add('visible');
    this.setAttribute('aria-hidden', 'false');
  }

  hide() {
    this.classList.remove('visible');
    this.setAttribute('aria-hidden', 'true');
  }

  syncButtonState() {
    if (!this.originalButton || !this.stickyButton) return;

    // Copy disabled state
    this.stickyButton.disabled = this.originalButton.disabled;

    // Copy text content
    const originalText = this.originalButton.querySelector('span');
    const stickyText = this.stickyButton.querySelector('span');

    if (originalText && stickyText) {
      stickyText.textContent = originalText.textContent;
    }

    // Copy aria-disabled
    const ariaDisabled = this.originalButton.getAttribute('aria-disabled');
    if (ariaDisabled) {
      this.stickyButton.setAttribute('aria-disabled', ariaDisabled);
    } else {
      this.stickyButton.removeAttribute('aria-disabled');
    }
  }

  observeOriginalButton() {
    if (!this.originalButton) return;

    // Watch for attribute changes on the original button
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          this.syncButtonState();
        }
      });
    });

    observer.observe(this.originalButton, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled']
    });

    // Watch for text changes
    const originalSpan = this.originalButton.querySelector('span');
    if (originalSpan) {
      const textObserver = new MutationObserver(() => {
        this.syncButtonState();
      });

      textObserver.observe(originalSpan, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }
  }

  handleStickyButtonClick(event) {
    event.preventDefault();

    if (this.originalButton && !this.originalButton.disabled) {
      // Trigger the original button click
      this.originalButton.click();

      // Optional: Scroll to top to show the cart notification
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScroll);
  }
}

customElements.define('sticky-add-to-cart', StickyAddToCart);
